import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchApi } from '../../services/api';
import LoadingPanel from '../../components/LoadingPanel';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Input } from '@progress/kendo-react-inputs';
import { Document, DocumentEntityType } from './Documents';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DocumentType } from 'TypeGen/Documents/document-type';
import { Button } from '@progress/kendo-react-buttons';
import { arrowLeftIcon, arrowRightIcon, hyperlinkOpenIcon } from '@progress/kendo-svg-icons';
import { useHotkeys } from 'react-hotkeys-hook';

interface Props {
    Document: Document;
    EntityType: DocumentEntityType
    EntityId: number,
    CloseDocumentModal: (requestDocuments: boolean) => void;
    PreviousDocument?: () => void;
    NextDocument?: () => void;
}

type IDTextValue = {
    ID: number,
    Text: string
};

type DocumentState = Document & { DocumentType: number };

const initializeDocument = (document: Document): DocumentState => ({
    ...document,
    DocumentType: document.DocumentType ?? 0
});

const DocumentEdit: React.FC<Props> = (props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [document, setDocument] = useState<DocumentState>(initializeDocument(props.Document));
    const isNewEntry = !props.Document.DocumentID;

    useEffect(() => {
        setDocument(initializeDocument(props.Document));
    }, [props.Document]);

    const documentTypes = useMemo<IDTextValue[]>(() => {
        const base: IDTextValue[] = [{ ID: 0, Text: "Miscellaneous" }];

        switch (props.EntityType) {
            case DocumentEntityType.Customer:
                base.push(
                    { ID: DocumentType.CertificateOfInsurance, Text: "Certificate of Insurance" },
                    { ID: DocumentType.CarrierPackets, Text: "Carrier Packets" },
                    { ID: DocumentType.Rates, Text: "Rates" },
                    { ID: DocumentType.FSC, Text: "FSC" },
                    { ID: DocumentType.CustomerContract, Text: "Customer Contract" },
                    { ID: DocumentType.BillingDocument, Text: "Billing Document" },
                    { ID: DocumentType.CustomerOnboard, Text: "Onboard" },
                    { ID: DocumentType.StandardOperatingProcedure, Text: "SOP" },
                    { ID: DocumentType.EscalationStandardOperatingProcedure, Text: "Escalation SOP" },
                    { ID: DocumentType.CustomerWelcomePacket, Text: "Customer Welcome Packet" },
                    { ID: DocumentType.AccessorialCharges, Text: "Accessorial Charges" },
                    { ID: DocumentType.W9, Text: "W-9" },
                );
                break;
            case DocumentEntityType.Carrier:
                base.push(
                    { ID: DocumentType.CertificateOfInsurance, Text: "Certificate of Insurance" },
                    { ID: DocumentType.CarrierPackets, Text: "Carrier Packets" },
                    { ID: DocumentType.CarrierBrokerageAgreement, Text: "Carrier Brokerage Agreement" },
                    { ID: DocumentType.NOA, Text: "Notice of Assignment" },
                    { ID: DocumentType.CarrierProfile, Text: "Carrier Profile" },
                    { ID: DocumentType.CtpatQuestionnaire, Text: "CTPAT Questionnaire" },
                    { ID: DocumentType.CtpatCertification, Text: "CTPAT Certification" },
                    { ID: DocumentType.BcaAddendum, Text: "BCA Addendum" },
                );
                break;
            case DocumentEntityType.Driver:
                base.push(
                    { ID: DocumentType.HazMat, Text: "HazMat" },
                    { ID: DocumentType.FAST, Text: "FAST" },
                    { ID: DocumentType.CSA, Text: "CSA" },
                    { ID: DocumentType.TWIC, Text: "TWIC" },
                    { ID: DocumentType.TSA, Text: "TSA" },
                    { ID: DocumentType.DriverPhoto, Text: "Driver Photo" },
                    { ID: DocumentType.Pet, Text: "Pet" },
                    { ID: DocumentType.HazMatTraining, Text: "HazMat Training" },
                    { ID: DocumentType.ApprovedPassenger, Text: "Approved Passenger" },
                    { ID: DocumentType.DODApproved, Text: "DOD Approved" },
                    { ID: DocumentType.SecurityThreatAssessment, Text: "STA Documentation" },
                    { ID: DocumentType.DriverLicense, Text: "Driver License" },
                );
                break;
            case DocumentEntityType.Vehicle:
                base.push(
                    { ID: DocumentType.VehiclePhoto, Text: "Vehicle Photo" },
                );
                break;
        }

        return base;
    }, [props.EntityType]);

    const openDocument = useCallback(() => {
        window.open(`/api/Document/Download/${props.Document.DocumentID}`, "_blank");
    }, [props.Document.DocumentID]);

    const deleteDocument = useCallback(() => {
        if (!window.confirm(`Are you sure you want to delete this document?`)) {
            return;
        }

        setIsLoading(true);
        fetchApi(`/api/Document/Delete/${props.Document.DocumentID}`, {}, 'DELETE')
            .then(() => props.CloseDocumentModal(true))
            .catch((e) => {
                // If not problem details
                if (!e?.status) alert('Unknown Error Occurred Deleting Document!');
            })
            .finally(() => setIsLoading(false));
    }, [props]);

    const updateDocument = useCallback(() => {
        setIsLoading(true);
        const data = {
            ...document,
            DocumentType: document.DocumentType === -1 ? null : document.DocumentType,
        };

        fetchApi(`/api/Document/Update/${props.EntityType}/${props.EntityId}`, data, 'POST')
            .then(() => props.CloseDocumentModal(true))
            .catch(() => {
                alert("Unknown Error Occurred Updating Document!");
                setIsLoading(false);
            });
    }, [document, props]);

    useHotkeys('left', () => {
        if (!props.PreviousDocument || isNewEntry) return;
        props.PreviousDocument();
    }, {
        enabled: !!props.PreviousDocument,
        preventDefault: true,
    }, [props.PreviousDocument, props.Document.DocumentID]);

    useHotkeys('right', () => {
        if (!props.NextDocument || isNewEntry) return;
        props.NextDocument();
    }, {
        enabled: !!props.NextDocument,
        preventDefault: true,
    }, [props.NextDocument, props.Document.DocumentID]);

    useHotkeys('delete', () => {
        deleteDocument();
    }, {
        enabled: !isLoading,
        preventDefault: true,
    }, [deleteDocument, isLoading]);

    useHotkeys('ctrl+s,mod+s', () => {
        updateDocument();
    }, {
        enabled: !isLoading,
        preventDefault: true,
        enableOnFormTags: true,
    }, [updateDocument, isLoading]);

    const selectedDocumentType = documentTypes.find(x => x.ID === document.DocumentType);

    return (
        <Dialog
            className='dialog-w11/12'
            onClose={() => props.CloseDocumentModal(false)}
            title={`${(isNewEntry) ? "New " : ""}Document: ${props.Document.Title}`}
        >
            {isLoading && <LoadingPanel />}
            <div className="row h-100">
                <div className="col-md-4">
                    <div className="px-3">
                        {((props.NextDocument || props.PreviousDocument) && !isNewEntry) && <div className="row">
                            <div className="form-group d-flex justify-content-between" style={{ width: "100%" }}>
                                <Button
                                    type="button"
                                    svgIcon={arrowLeftIcon}
                                    onClick={() => props.PreviousDocument?.()}
                                    disabled={!props.PreviousDocument}
                                >
                                    Previous
                                </Button>
                                <Button
                                    type="button"
                                    svgIcon={arrowRightIcon}
                                    onClick={() => props.NextDocument?.()}
                                    disabled={!props.NextDocument}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>}
                        <div className="row">
                            <div className="form-group" style={{ width: "100%" }}>
                                <label>Name</label>
                                <Input
                                    className="form-control"
                                    maxLength={100}
                                    value={document.Title}
                                    onChange={(e) => setDocument({ ...document, Title: e.target.value.toString() })}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="form-group" style={{ width: "100%" }}>
                                <label>Document Type</label>
                                <DropDownList
                                    style={{ width: '100%' }}
                                    data={documentTypes}
                                    textField="Text"
                                    value={selectedDocumentType}
                                    onChange={(e) => setDocument({ ...document, DocumentType: e.target.value.ID })}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-inline" style={{ width: "100%" }}>
                                <Button type="button" themeColor="error" className="float-left" onClick={deleteDocument}>Delete</Button>
                                <Button type="button" themeColor="info" svgIcon={hyperlinkOpenIcon} className="mx-auto" onClick={openDocument}>Open</Button>
                                <Button type="button" themeColor="primary" className="float-right" onClick={updateDocument}>Save</Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-8 h-100">
                    {props.Document.CannotDisplay
                        ? <span className="text-warning">Cannot display this document type. Click Open to download instead.</span>
                        : props.Document.IsImage
                            ? <img src={`/api/Document/View/${props.Document.DocumentID}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            : <embed src={`/api/Document/View/${props.Document.DocumentID}`} width="100%" height="100%" style={{ minHeight: '600px' }} />}
                </div>
            </div>
        </Dialog>
    );
}

export default DocumentEdit;
